import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { hrApi } from '@/api';
import { EmployeeSelect, HrShiftSelect, OvertimeTypeSelect, ShiftTypeSelect } from '@/components/selects';
import { PageHeader } from '@/components/layout/PageHeader';
import { FormActions } from '@/components/ui/FormActions';
import { Tooltip } from '@/components/ui/Tooltip';
import { Input } from '@/components/ui/Input';
import { DatePicker } from '@/components/ui/DatePicker';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { FormGrid } from '@/components/ui/FormGrid';
import { RecordDataTable } from '@/components/ui/RecordDataTable';
import { ViewModeToggle, type ViewMode } from '@/components/ui/ViewModeToggle';
import {
  HrEmployeeScheduleGrid,
  hrEmployeeInitials,
  hrShiftBlockStyle,
  type HrScheduleEmployee,
} from '@/modules/hr/components/HrEmployeeScheduleGrid';
import { hrDateKey, hrMonthRange, hrShiftDurationHours } from '@/modules/hr/components/hrCalendarUtils';
import { HrScheduleCalendarLegend } from '@/modules/hr/components/HrScheduleCalendarLegend';
import { useEmployeeScheduleCalendar } from '@/modules/hr/hooks/useEmployeeScheduleCalendar';
import { dataTablePageProps, paginateClientList, usePagination } from '@/hooks/usePagination';
import { useEmployeeOptions } from '@/hooks/useEmployeeOptions';
import type { HrShift } from '@/types/hr';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
import { cn } from '@/utils/cn';

interface HrRosterRow {
  id?: number;
  employeeId: number;
  shiftId?: number;
  rosterDate: string;
}

function SectionTable({ children }: { children: ReactNode }) {
  return <div className="border-t border-slate-200 pt-4 dark:border-slate-700">{children}</div>;
}

function formatShiftTimes(start?: string, end?: string): string {
  if (!start && !end) return '';
  if (start && end) return `${start.slice(0, 5)} – ${end.slice(0, 5)}`;
  return (start ?? end ?? '').slice(0, 5);
}

export function HrShiftsPage() {
  const shiftsPagination = usePagination();
  const { employees, getLabel } = useEmployeeOptions({ hrScoped: true });
  const [rosterViewMode, setRosterViewMode] = useState<ViewMode>('calendar');
  const [rosterViewDate, setRosterViewDate] = useState(() => new Date());

  const [shifts, setShifts] = useState<HrShift[]>([]);
  const [overtime, setOvertime] = useState<unknown[]>([]);
  const [roster, setRoster] = useState<HrRosterRow[]>([]);
  const [rosterLoading, setRosterLoading] = useState(false);

  const [shiftName, setShiftName] = useState('');
  const [shiftType, setShiftType] = useState('');
  const [shiftStart, setShiftStart] = useState('09:00');
  const [shiftEnd, setShiftEnd] = useState('18:00');

  const [employeeId, setEmployeeId] = useState('');
  const [shiftId, setShiftId] = useState('');
  const [rosterDate, setRosterDate] = useState('');

  const [otEmployeeId, setOtEmployeeId] = useState('');
  const [otDate, setOtDate] = useState('');
  const [otHours, setOtHours] = useState('2');
  const [otType, setOtType] = useState('');

  const [assignEmployeeId, setAssignEmployeeId] = useState('');
  const [assignShiftId, setAssignShiftId] = useState('');
  const [assignEffectiveFrom, setAssignEffectiveFrom] = useState('');
  const [assignEffectiveTo, setAssignEffectiveTo] = useState('');

  const shiftById = useMemo(() => new Map(shifts.map((s) => [s.id, s])), [shifts]);

  const loadShiftsAndOt = useCallback(async () => {
    try {
      const shiftRows = await hrApi.listShifts();
      setShifts(shiftRows);
      shiftsPagination.applyClientCount(shiftRows.length);
      setOvertime(await hrApi.listOvertime());
    } catch (err) {
      showApiError(err, 'Failed to load shift data');
    }
  }, [shiftsPagination]);

  const loadRoster = useCallback(async () => {
    setRosterLoading(true);
    try {
      const range = rosterViewMode === 'calendar' ? hrMonthRange(rosterViewDate) : undefined;
      const rows = (await hrApi.listRoster(range?.start, range?.end)) as HrRosterRow[];
      setRoster(rows);
    } catch (err) {
      showApiError(err, 'Failed to load roster');
    } finally {
      setRosterLoading(false);
    }
  }, [rosterViewMode, rosterViewDate]);

  useEffect(() => {
    loadShiftsAndOt();
  }, [loadShiftsAndOt]);

  useEffect(() => {
    loadRoster();
  }, [loadRoster]);

  const createShift = async () => {
    if (!shiftName.trim() || !shiftType) {
      notify.error('Shift name and type are required');
      return;
    }
    try {
      await hrApi.createShift({
        name: shiftName.trim(),
        shiftType,
        startTime: shiftStart,
        endTime: shiftEnd,
        breakMinutes: 60,
        workingDaysMask: 31,
      });
      notify.success('Shift created');
      setShiftName('');
      setShiftType('');
      loadShiftsAndOt();
    } catch (err) {
      showApiError(err, 'Create shift failed');
    }
  };

  const assignRoster = async () => {
    if (!employeeId || !shiftId || !rosterDate) return;
    try {
      await hrApi.assignRoster({
        employeeId: Number(employeeId),
        shiftId: Number(shiftId),
        rosterDate,
      });
      notify.success('Roster assigned');
      setEmployeeId('');
      setShiftId('');
      setRosterDate('');
      loadRoster();
    } catch (err) {
      showApiError(err, 'Roster assign failed');
    }
  };

  const assignEmployeeShift = async () => {
    if (!assignEmployeeId || !assignShiftId || !assignEffectiveFrom) {
      notify.error('Employee, shift, and effective-from date are required');
      return;
    }
    try {
      await hrApi.assignEmployeeShift({
        employeeId: Number(assignEmployeeId),
        shiftId: Number(assignShiftId),
        effectiveFrom: assignEffectiveFrom,
        effectiveTo: assignEffectiveTo || undefined,
      });
      notify.success('Shift assignment saved');
      setAssignEmployeeId('');
      setAssignShiftId('');
      setAssignEffectiveFrom('');
      setAssignEffectiveTo('');
    } catch (err) {
      showApiError(err, 'Shift assignment failed');
    }
  };

  const requestOt = async () => {
    if (!otEmployeeId || !otDate || !otHours || !otType) {
      notify.error('Employee, date, hours, and overtime type are required');
      return;
    }
    const hours = Number(otHours);
    if (Number.isNaN(hours) || hours <= 0) {
      notify.error('Overtime hours must be greater than zero');
      return;
    }
    try {
      await hrApi.requestOvertime({
        employeeId: Number(otEmployeeId),
        overtimeDate: otDate,
        minutes: Math.round(hours * 60),
        overtimeType: otType,
      });
      notify.success('Overtime requested');
      setOtEmployeeId('');
      setOtDate('');
      setOtHours('2');
      setOtType('');
      loadShiftsAndOt();
    } catch (err) {
      showApiError(err, 'Overtime request failed');
    }
  };

  const rosterByEmployeeDay = useMemo(() => {
    const map = new Map<number, Map<string, HrRosterRow[]>>();
    for (const row of roster) {
      const key = row.rosterDate?.slice(0, 10);
      if (!key) continue;
      const byDay = map.get(row.employeeId) ?? new Map<string, HrRosterRow[]>();
      const list = byDay.get(key) ?? [];
      list.push(row);
      byDay.set(key, list);
      map.set(row.employeeId, byDay);
    }
    return map;
  }, [roster]);

  const rosterEmployeeIds = useMemo(() => new Set(roster.map((r) => r.employeeId)), [roster]);

  const scheduleEmployees = useMemo((): HrScheduleEmployee[] => {
    const rostered = employees.filter((e) => rosterEmployeeIds.has(e.id));
    const source = rostered.length > 0 ? rostered : employees;
    return [...source]
      .sort((a, b) => {
        const an = `${a.firstName ?? ''} ${a.lastName ?? ''}`.trim();
        const bn = `${b.firstName ?? ''} ${b.lastName ?? ''}`.trim();
        return an.localeCompare(bn);
      })
      .map((employee) => ({
        id: employee.id,
        name: [employee.firstName, employee.lastName].filter(Boolean).join(' ') || getLabel(employee.id, true),
        subtitle: employee.employeeCode ?? undefined,
        initials: hrEmployeeInitials(employee.firstName, employee.lastName),
      }));
  }, [employees, getLabel, rosterEmployeeIds]);

  const scheduleEmployeeIds = useMemo(() => scheduleEmployees.map((e) => e.id), [scheduleEmployees]);
  const scheduleCalendar = useEmployeeScheduleCalendar({
    viewDate: rosterViewDate,
    employeeIds: scheduleEmployeeIds,
    enabled: rosterViewMode === 'calendar' && scheduleEmployeeIds.length > 0,
  });

  const employeeMonthHours = useMemo(() => {
    const totals = new Map<number, number>();
    for (const row of roster) {
      const shift = row.shiftId ? shiftById.get(row.shiftId) : undefined;
      const hours = hrShiftDurationHours(shift?.startTime, shift?.endTime);
      totals.set(row.employeeId, (totals.get(row.employeeId) ?? 0) + hours);
    }
    return totals;
  }, [roster, shiftById]);

  const handleScheduleCellClick = (empId: number, day: Date) => {
    setEmployeeId(String(empId));
    setRosterDate(hrDateKey(day));
  };

  const shiftCols: Column<HrShift>[] = [
    { key: 'id', header: '#' },
    { key: 'name', header: 'Name' },
    { key: 'shiftType', header: 'Type' },
    { key: 'startTime', header: 'Start' },
    { key: 'endTime', header: 'End' },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Shifts & roster"
        description="Shift definitions, roster assignments, and overtime"
        breadcrumbs={[{ label: 'HR', to: '/hr' }, { label: 'Shifts' }]}
      />

      <section className="card space-y-4 p-4">
        <h3 className="font-semibold text-slate-900 dark:text-slate-100">Shift definitions</h3>
        <FormGrid columns={4}>
          <Input label="Shift name" value={shiftName} onChange={(e) => setShiftName(e.target.value)} />
          <ShiftTypeSelect value={shiftType} onChange={setShiftType} required />
          <Input label="Start time" type="time" value={shiftStart} onChange={(e) => setShiftStart(e.target.value)} />
          <Input label="End time" type="time" value={shiftEnd} onChange={(e) => setShiftEnd(e.target.value)} />
        </FormGrid>
        <FormActions
          primaryLabel="Create shift"
          onPrimary={() => void createShift()}
          onReset={() => {
            setShiftName('');
            setShiftType('');
            setShiftStart('09:00');
            setShiftEnd('18:00');
          }}
        />
        <SectionTable>
          <DataTable
            columns={shiftCols}
            data={paginateClientList(shifts, shiftsPagination.page, shiftsPagination.size)}
            keyField="id"
            emptyTitle="No shifts yet"
            {...dataTablePageProps(shiftsPagination)}
          />
        </SectionTable>
      </section>

      <section className="card space-y-4 p-4">
        <h3 className="font-semibold text-slate-900 dark:text-slate-100">Employee shift assignments</h3>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Long-term shift pattern (used for expected hours, leave, attendance, and overtime). Use roster below for one-off day overrides.
        </p>
        <FormGrid columns={4}>
          <EmployeeSelect value={assignEmployeeId} onChange={setAssignEmployeeId} hrScoped />
          <HrShiftSelect value={assignShiftId} onChange={setAssignShiftId} />
          <DatePicker label="Effective from" value={assignEffectiveFrom} onChange={setAssignEffectiveFrom} />
          <DatePicker label="Effective to (optional)" value={assignEffectiveTo} onChange={setAssignEffectiveTo} />
        </FormGrid>
        <FormActions
          primaryLabel="Assign shift"
          onPrimary={() => void assignEmployeeShift()}
          onReset={() => {
            setAssignEmployeeId('');
            setAssignShiftId('');
            setAssignEffectiveFrom('');
            setAssignEffectiveTo('');
          }}
        />
      </section>

      <section className="card space-y-4 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-semibold text-slate-900 dark:text-slate-100">Roster planning</h3>
          <ViewModeToggle value={rosterViewMode} onChange={setRosterViewMode} />
        </div>
        <FormGrid columns={3}>
          <EmployeeSelect value={employeeId} onChange={setEmployeeId} hrScoped />
          <HrShiftSelect value={shiftId} onChange={setShiftId} />
          <DatePicker label="Date" value={rosterDate} onChange={setRosterDate} />
        </FormGrid>
        <FormActions
          primaryLabel="Assign roster"
          onPrimary={() => void assignRoster()}
          onReset={() => {
            setEmployeeId('');
            setShiftId('');
            setRosterDate('');
          }}
        />
        <SectionTable>
          {rosterViewMode === 'list' ? (
            <RecordDataTable data={roster} embedded emptyTitle="No roster assignments yet" loading={rosterLoading} />
          ) : (
            <HrEmployeeScheduleGrid
              viewDate={rosterViewDate}
              onViewDateChange={setRosterViewDate}
              employees={scheduleEmployees}
              loading={rosterLoading || scheduleCalendar.loading}
              onCellClick={handleScheduleCellClick}
              getEmployeeTotal={(empId) => employeeMonthHours.get(empId) ?? null}
              getCellBackgroundClass={scheduleCalendar.getCellBackgroundClass}
              getHeaderBackgroundClass={scheduleCalendar.getHeaderBackgroundClass}
              getCellTitle={scheduleCalendar.getDayTooltip}
              calendarLegend={<HrScheduleCalendarLegend />}
              emptyMessage="No employees found — add employees first, then assign shifts."
              renderCell={(empId, _day, dayKey) => {
                const items = rosterByEmployeeDay.get(empId)?.get(dayKey) ?? [];
                if (items.length === 0) {
                  return <span className="text-[10px] text-slate-300 dark:text-slate-600">+</span>;
                }
                return (
                  <div className="flex flex-col gap-0.5">
                    {items.map((row, idx) => {
                      const shift = row.shiftId ? shiftById.get(row.shiftId) : undefined;
                      const label = shift?.name ?? (row.shiftId ? `Shift ${row.shiftId}` : 'Assigned');
                      const times = formatShiftTimes(shift?.startTime, shift?.endTime);
                      const tip = `${label}${times ? ` · ${times}` : ''}`;
                      return (
                        <Tooltip key={row.id ?? `${empId}-${dayKey}-${idx}`} content={tip}>
                          <div
                            className={cn(
                              'rounded border px-1 py-0.5 text-[10px] leading-tight shadow-sm',
                              hrShiftBlockStyle(row.shiftId),
                            )}
                          >
                            <div className="truncate font-semibold">{label}</div>
                            {times ? <div className="truncate opacity-90">{times}</div> : null}
                          </div>
                        </Tooltip>
                      );
                    })}
                  </div>
                );
              }}
            />
          )}
        </SectionTable>
      </section>

      <section className="card space-y-4 p-4">
        <h3 className="font-semibold text-slate-900 dark:text-slate-100">Overtime requests</h3>
        <FormGrid columns={4}>
          <EmployeeSelect value={otEmployeeId} onChange={setOtEmployeeId} hrScoped />
          <DatePicker label="Date" value={otDate} onChange={setOtDate} />
          <Input label="Hours" type="number" min={0.5} step={0.5} value={otHours} onChange={(e) => setOtHours(e.target.value)} />
          <OvertimeTypeSelect value={otType} onChange={setOtType} required />
        </FormGrid>
        <FormActions
          primaryLabel="Request OT"
          onPrimary={() => void requestOt()}
          onReset={() => {
            setOtEmployeeId('');
            setOtDate('');
            setOtHours('2');
            setOtType('');
          }}
        />
        <SectionTable>
          <RecordDataTable data={overtime} embedded emptyTitle="No overtime requests yet" />
        </SectionTable>
      </section>
    </div>
  );
}
